export type ThemeSetting = 'auto' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

// OS設定からテーマを解決
export function resolveTheme(setting: ThemeSetting): Theme {
  if (setting === 'auto') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return setting;
}

export interface ThemeColors {
  // Background colors
  background: {
    primary: string;    // Main background
    secondary: string;  // Card backgrounds, modals
    tertiary: string;   // Nested elements
    hover: string;      // Hover states
    overlay: string;    // Overlays and backdrops
  };
  
  // Text colors
  text: {
    primary: string;    // Main text
    secondary: string;  // Secondary text
    tertiary: string;   // Muted text
    inverse: string;    // Inverted text
  };
  
  // Border colors
  border: {
    primary: string;
    secondary: string;
    hover: string;
  };
  
  // Brand colors (green theme)
  brand: {
    primary: string;    // #0DD699
    hover: string;      // #3DDDA8
    active: string;     // #0BB584
    dark: string;       // #08795B
    darker: string;     // #065A46
    light: string;      // #0DD699 with opacity
  };
  
  // Status colors
  status: {
    error: string;
    errorBg: string;
    warning: string;
    warningBg: string;
    success: string;
    successBg: string;
    info: string;
    infoBg: string;
  };
  
  // Card type colors
  cardType: {
    character: string;
    support: string;
    environment: string;
  };
  
  // Special effects
  effects: {
    shadow: string;
    shadowHover: string;
    glow: string;
  };
  
  // Blend UI colors
  blend: {
    border: string;
    borderHover: string;
    text: string;
  };
}

export const themes: Record<Theme, ThemeColors> = {
  dark: {
    background: {
      primary: 'rgb(39, 39, 42)',      // zinc-800
      secondary: 'rgb(24, 24, 27)',     // zinc-900 - スケルトン統一のため黒っぽく変更
      tertiary: 'rgb(24, 24, 27)',      // zinc-900
      hover: 'rgba(107, 114, 128, 0.1)', // gray-500 with opacity
      overlay: 'rgba(0, 0, 0, 0.6)',
    },
    text: {
      primary: 'rgb(255, 255, 255)',    // white
      secondary: 'rgb(209, 213, 219)',  // gray-300
      tertiary: 'rgb(107, 114, 128)',   // gray-500
      inverse: 'rgb(0, 0, 0)',          // black
    },
    border: {
      primary: 'rgb(75, 85, 99)',       // gray-600
      secondary: 'rgba(75, 85, 99, 0.5)', // gray-600 with opacity
      hover: 'rgb(107, 114, 128)',      // gray-500
    },
    brand: {
      primary: '#0DD699',
      hover: '#3DDDA8',
      active: '#0BB584',
      dark: '#08795B',
      darker: '#065A46',
      light: 'rgba(13, 214, 153, 0.2)',
    },
    status: {
      error: 'rgb(248, 113, 113)',      // red-400
      errorBg: 'rgba(239, 68, 68, 0.1)', // red-500 with opacity
      warning: 'rgb(251, 191, 36)',     // amber-400
      warningBg: 'rgba(245, 158, 11, 0.1)', // amber-500 with opacity
      success: '#0DD699',                // brand green
      successBg: 'rgba(13, 214, 153, 0.1)',
      info: 'rgb(96, 165, 250)',        // blue-400
      infoBg: 'rgba(59, 130, 246, 0.1)', // blue-500 with opacity
    },
    cardType: {
      character: 'rgb(251, 191, 36)',   // amber-400
      support: 'rgb(56, 189, 248)',     // sky-400
      environment: 'rgb(52, 211, 153)',  // emerald-400
    },
    effects: {
      shadow: 'rgba(0, 0, 0, 0.4)',
      shadowHover: 'rgba(13, 214, 153, 0.4)',
      glow: 'rgba(13, 214, 153, 0.5)',
    },
    blend: {
      border: '#0DD699',
      borderHover: '#0DD699',
      text: '#0DD699',
    },
  },
  
  light: {
    background: {
      primary: '#E3DDDE',               // More gray-toned background
      secondary: '#D7D0D2',             // Slightly darker gray-pink
      tertiary: '#CCC3C6',              // Darker gray-pink
      hover: 'rgba(140, 130, 135, 0.1)', // Gray with slight pink tint
      overlay: 'rgba(0, 0, 0, 0.4)',
    },
    text: {
      primary: 'rgb(31, 41, 55)',       // gray-800
      secondary: 'rgb(75, 85, 99)',     // gray-600
      tertiary: 'rgb(107, 114, 128)',   // gray-500
      inverse: 'rgb(255, 255, 255)',    // white
    },
    border: {
      primary: '#C5B8BB',               // Gray-toned border with subtle pink
      secondary: 'rgba(197, 184, 187, 0.5)',
      hover: '#B8A9AD',                 // Darker gray-pink
    },
    brand: {
      primary: '#0A9770',                // Darker green for better contrast on light bg
      hover: '#0DD699',
      active: '#08795B',
      dark: '#065A46',
      darker: '#043D30',
      light: 'rgba(13, 214, 153, 0.15)',
    },
    status: {
      error: 'rgb(220, 38, 38)',        // red-600 for better contrast
      errorBg: 'rgba(254, 226, 226, 0.8)', // red-100 with opacity
      warning: 'rgb(217, 119, 6)',      // amber-600 for better contrast
      warningBg: 'rgba(254, 243, 199, 0.8)', // amber-100 with opacity
      success: '#0A9770',                // Darker green for contrast
      successBg: 'rgba(13, 214, 153, 0.15)',
      info: 'rgb(37, 99, 235)',         // blue-600 for better contrast
      infoBg: 'rgba(219, 234, 254, 0.8)', // blue-100 with opacity
    },
    cardType: {
      character: 'rgb(217, 119, 6)',    // amber-600
      support: 'rgb(2, 132, 199)',      // sky-600
      environment: 'rgb(5, 150, 105)',   // emerald-600
    },
    effects: {
      shadow: 'rgba(0, 0, 0, 0.15)',
      shadowHover: 'rgba(13, 214, 153, 0.3)',
      glow: 'rgba(13, 214, 153, 0.3)',
    },
    blend: {
      border: '#0BB584',
      borderHover: '#08795B',
      text: '#0BB584',
    },
  },
};

// Helper function to get CSS variables for a theme
export function getThemeCSSVariables(theme: Theme): Record<string, string> {
  const colors = themes[theme];
  
  return {
    '--bg-primary': colors.background.primary,
    '--bg-secondary': colors.background.secondary,
    '--bg-tertiary': colors.background.tertiary,
    '--bg-hover': colors.background.hover,
    '--bg-overlay': colors.background.overlay,
    
    '--text-primary': colors.text.primary,
    '--text-secondary': colors.text.secondary,
    '--text-tertiary': colors.text.tertiary,
    '--text-inverse': colors.text.inverse,
    
    '--border-primary': colors.border.primary,
    '--border-secondary': colors.border.secondary,
    '--border-hover': colors.border.hover,
    
    '--brand-primary': colors.brand.primary,
    '--brand-hover': colors.brand.hover,
    '--brand-active': colors.brand.active,
    '--brand-dark': colors.brand.dark,
    '--brand-darker': colors.brand.darker,
    '--brand-light': colors.brand.light,
    
    '--status-error': colors.status.error,
    '--status-error-bg': colors.status.errorBg,
    '--status-warning': colors.status.warning,
    '--status-warning-bg': colors.status.warningBg,
    '--status-success': colors.status.success,
    '--status-success-bg': colors.status.successBg,
    '--status-info': colors.status.info,
    '--status-info-bg': colors.status.infoBg,
    
    '--card-character': colors.cardType.character,
    '--card-support': colors.cardType.support,
    '--card-environment': colors.cardType.environment,
    
    '--effect-shadow': colors.effects.shadow,
    '--effect-shadow-hover': colors.effects.shadowHover,
    '--effect-glow': colors.effects.glow,
    
    '--blend-border': colors.blend.border,
    '--blend-border-hover': colors.blend.borderHover,
    '--blend-text': colors.blend.text,
  };
}

// Helper function to apply theme to document
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  const cssVars = getThemeCSSVariables(theme);
  
  Object.entries(cssVars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  
  // Add theme class to body
  document.body.classList.remove('theme-light', 'theme-dark');
  document.body.classList.add(`theme-${theme}`);
}
